import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from './Icons';
interface QuoteFormProps {
  lang: 'en' | 'es';
}
export const QuoteForm: React.FC<QuoteFormProps> = ({
  lang
}) => {
  const [formLang, setFormLang] = useState<'en' | 'es'>(lang);
  const [condition, setCondition] = useState<string>('new');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const content = {
    en: {
      title: 'Get Your Free Quote',
      subtitle: "I'll contact you the same day",
      name: 'Full Name',
      phone: 'Phone',
      email: 'Email',
      prefLang: 'Preferred Language',
      brand: 'Brand Interest',
      condition: 'New or Pre-Owned?',
      budget: 'Budget',
      message: 'Message (Optional)',
      submit: 'Send My Request',
      sending: 'Sending...',
      footer: 'Simon personally responds to every inquiry within hours.',
      successTitle: 'Request Sent!',
      successMsg: 'Simon will contact you within hours. Thank you!',
      errorMsg: 'Something went wrong. Please try again.',
      sendAnother: 'Send Another Request',
      brands: ['Subaru', 'Toyota', 'Honda', 'Ford', 'Chevrolet', 'Nissan', 'Hyundai', 'Kia', 'BMW', 'Mercedes-Benz', 'Mazda', 'Jeep', 'Other Brand'],
      conditions: [{
        id: 'new',
        label: 'New'
      }, {
        id: 'used',
        label: 'Pre-Owned'
      }, {
        id: 'both',
        label: 'Open to Both'
      }],
      budgets: ['Under $20K', '$20K–$35K', '$35K–$50K', '$50K+', 'Not sure']
    },
    es: {
      title: 'Cotización Gratis',
      subtitle: 'Te contacto el mismo día',
      name: 'Nombre completo',
      phone: 'Teléfono',
      email: 'Correo electrónico',
      prefLang: 'Idioma preferido',
      brand: 'Marca de Interés',
      condition: '¿Nuevo o Usado?',
      budget: 'Presupuesto',
      message: 'Mensaje (Opcional)',
      submit: 'Enviar Mi Solicitud',
      sending: 'Enviando...',
      footer: 'Simon responde personalmente a cada solicitud en pocas horas.',
      successTitle: '¡Solicitud Enviada!',
      successMsg: 'Simon te contactará en pocas horas. ¡Gracias!',
      errorMsg: 'Algo salió mal. Inténtalo de nuevo.',
      sendAnother: 'Enviar Otra Solicitud',
      brands: ['Subaru', 'Toyota', 'Honda', 'Ford', 'Chevrolet', 'Nissan', 'Hyundai', 'Kia', 'BMW', 'Mercedes-Benz', 'Mazda', 'Jeep', 'Otra Marca'],
      conditions: [{
        id: 'new',
        label: 'Nuevo'
      }, {
        id: 'used',
        label: 'Usado'
      }, {
        id: 'both',
        label: 'Ambos'
      }],
      budgets: ['Menos de $20K', '$20K–$35K', '$35K–$50K', '$50K+', 'No sé']
    }
  };
  const t = content[lang];
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    const formData = new FormData(e.currentTarget);
    formData.append('condition', condition);
    formData.append('preferred_language', formLang === 'en' ? 'English' : 'Español');
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formData
      });
      const data = await response.json();
      if (data.success) {
        setIsSuccess(true);
      } else {
        setError(t.errorMsg);
      }
    } catch {
      setError(t.errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };
  if (isSuccess) {
    return <section id="quote-form" className="py-24 px-6 bg-formBg border-y border-border" data-id="element-102">
        <div className="max-w-3xl mx-auto" data-id="element-103">
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="bg-card p-10 md:p-16 rounded-xl border border-border text-center" data-id="element-104">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-8" data-id="element-105">
              <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#0057FF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-106">
                <path d="M20 6L9 17l-5-5" data-id="element-107" />
              </svg>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-108">
              {t.successTitle}
            </h2>
            <p className="text-[#aaaaaa] text-lg mb-10" data-id="element-109">{t.successMsg}</p>
            <button onClick={() => setIsSuccess(false)} className="px-8 py-3 bg-transparent border border-white/20 hover:border-white/50 text-white rounded-full font-medium transition-colors" data-id="element-110">
              {t.sendAnother}
            </button>
          </motion.div>
        </div>
      </section>;
  }
  return <section id="quote-form" className="py-24 px-6 bg-formBg border-y border-border" data-id="element-111">
      <div className="max-w-3xl mx-auto" data-id="element-112">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-12" data-id="element-113">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-114">{t.title}</h2>
          <p className="text-[#aaaaaa] text-lg" data-id="element-115">{t.subtitle}</p>
        </motion.div>

        <motion.form initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.1
      }} className="space-y-6 bg-card p-6 md:p-10 rounded-xl border border-border" onSubmit={handleSubmit} data-id="element-116">
          {/* Web3Forms Access Key */}
          <input type="hidden" name="access_key" value="2840eb19-a382-4722-a25c-1778cd0c698d" data-id="element-117" />
          <input type="hidden" name="subject" value="New Quote Request — SimonPro.vip" data-id="element-118" />
          <input type="hidden" name="from_name" value="Simon Pro Website" data-id="element-119" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-120">
            <div className="space-y-2" data-id="element-121">
              <label className="text-sm font-medium text-white/80" data-id="element-122">
                {t.name}
              </label>
              <input type="text" name="name" className="w-full bg-inputBg border border-border rounded-lg px-4 py-3 text-white focus:outline-none focus:border-primary transition-colors" required data-id="element-123" />
            </div>
            <div className="space-y-2" data-id="element-124">
              <label className="text-sm font-medium text-white/80" data-id="element-125">
                {t.phone}
              </label>
              <input type="tel" name="phone" className="w-full bg-inputBg border border-border rounded-lg px-4 py-3 text-white focus:outline-none focus:border-primary transition-colors" required data-id="element-126" />
            </div>
          </div>

          <div className="space-y-2" data-id="element-127">
            <label className="text-sm font-medium text-white/80" data-id="element-128">
              {t.email}
            </label>
            <input type="email" name="email" className="w-full bg-inputBg border border-border rounded-lg px-4 py-3 text-white focus:outline-none focus:border-primary transition-colors" required data-id="element-129" />
          </div>

          <div className="space-y-2" data-id="element-130">
            <label className="text-sm font-medium text-white/80" data-id="element-131">
              {t.prefLang}
            </label>
            <div className="flex bg-inputBg border border-border rounded-lg p-1" data-id="element-132">
              <button type="button" onClick={() => setFormLang('en')} className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${formLang === 'en' ? 'bg-card text-white shadow-sm border border-border' : 'text-[#aaaaaa] hover:text-white'}`} data-id="element-133">
                English
              </button>
              <button type="button" onClick={() => setFormLang('es')} className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${formLang === 'es' ? 'bg-card text-white shadow-sm border border-border' : 'text-[#aaaaaa] hover:text-white'}`} data-id="element-134">
                Español
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-135">
            <div className="space-y-2" data-id="element-136">
              <label className="text-sm font-medium text-white/80" data-id="element-137">
                {t.brand}
              </label>
              <select name="brand_interest" className="w-full bg-inputBg border border-border rounded-lg px-4 py-3 text-white focus:outline-none focus:border-primary transition-colors appearance-none" data-id="element-138">
                <option value="" disabled selected data-id="element-139">
                  Select...
                </option>
                {t.brands.map(brand => <option key={brand} value={brand} data-id="element-140">
                    {brand}
                  </option>)}
              </select>
            </div>
            <div className="space-y-2" data-id="element-141">
              <label className="text-sm font-medium text-white/80" data-id="element-142">
                {t.budget}
              </label>
              <select name="budget" className="w-full bg-inputBg border border-border rounded-lg px-4 py-3 text-white focus:outline-none focus:border-primary transition-colors appearance-none" data-id="element-143">
                <option value="" disabled selected data-id="element-144">
                  Select...
                </option>
                {t.budgets.map(budget => <option key={budget} value={budget} data-id="element-145">
                    {budget}
                  </option>)}
              </select>
            </div>
          </div>

          <div className="space-y-3" data-id="element-146">
            <label className="text-sm font-medium text-white/80" data-id="element-147">
              {t.condition}
            </label>
            <div className="flex flex-wrap gap-3" data-id="element-148">
              {t.conditions.map(cond => <button key={cond.id} type="button" onClick={() => setCondition(cond.id)} className={`px-5 py-2.5 rounded-full text-sm font-medium border transition-all ${condition === cond.id ? 'bg-primary/10 border-primary text-primary' : 'bg-inputBg border-border text-[#aaaaaa] hover:border-white/30 hover:text-white'}`} data-id="element-149">
                  {cond.label}
                </button>)}
            </div>
          </div>

          <div className="space-y-2" data-id="element-150">
            <label className="text-sm font-medium text-white/80" data-id="element-151">
              {t.message}
            </label>
            <textarea name="message" rows={3} className="w-full bg-inputBg border border-border rounded-lg px-4 py-3 text-white focus:outline-none focus:border-primary transition-colors resize-none" data-id="element-152"></textarea>
          </div>

          {error && <p className="text-red-400 text-sm text-center" data-id="element-153">{error}</p>}

          <button type="submit" disabled={isSubmitting} className="w-full h-[52px] bg-primary hover:bg-blue-600 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-lg font-medium flex items-center justify-center gap-2 transition-colors mt-8" data-id="element-154">
            {isSubmitting ? t.sending : t.submit}
            {!isSubmitting && <ArrowRight size={20} data-id="element-155" />}
          </button>

          <p className="text-center text-sm text-[#666666] mt-4" data-id="element-156">{t.footer}</p>
        </motion.form>
      </div>
    </section>;
};